import { useState } from 'react';
import {
  useCreateMtoLogistOrderMutation,
  useCreateMtoLotFromDeliveryMutation,
} from '@shared/api';
import type { MtoDelivery, MtoPurchaseOrder } from '@entities/mto';
import { apiErrorMessage, fmtDate } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { qtyWithUnit, supplySource } from '../helpers';
import { Muted, Table, TableCaption, TableScroll } from '../styles';

/**
 * Блок «Поставки (план-факт)» (модуль 2.1) — две таблицы read-only: заказы на
 * закупку (план) и приходы/поступления (факт). HITL по расхождениям (просрочки,
 * недопоставки) идёт через общий блок «Аномалии» — здесь не дублируется.
 */
export function SupplySection({
  orders,
  deliveries,
  canWrite,
  projectId,
  onError,
}: {
  orders: MtoPurchaseOrder[];
  deliveries: MtoDelivery[];
  canWrite: boolean;
  projectId?: string | null;
  onError: (msg: string) => void;
}) {
  const [createLogistOrder, { isLoading: creatingLogist }] = useCreateMtoLogistOrderMutation();
  const [createLotFromDelivery, { isLoading: creatingLot }] = useCreateMtoLotFromDeliveryMutation();
  const [notice, setNotice] = useState<string | null>(null);

  if (orders.length === 0 && deliveries.length === 0) {
    return <Muted>Заказы и приходы появятся после завершения обработки.</Muted>;
  }
  return (
    <>
      {notice && (
        <div style={{ marginBottom: 12 }}>
          <Badge $tone="success">{notice}</Badge>
        </div>
      )}
      <TableCaption>
        Заказы на закупку (план) <Badge $tone="neutral">{orders.length}</Badge>
      </TableCaption>
      {orders.length === 0 ? (
        <Muted>Заказы на закупку не загружены.</Muted>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th scope="col">Наименование</th>
                <th scope="col">№ заказа</th>
                <th scope="col">Поставщик</th>
                <th scope="col" className="num">
                  Кол-во
                </th>
                <th scope="col">Срок</th>
                <th scope="col">Источник</th>
                {canWrite && <th scope="col">Перевозка</th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>{o.position_name}</td>
                  <td>{o.order_no ?? '—'}</td>
                  <td>{o.supplier ?? '—'}</td>
                  <td className="num">{qtyWithUnit(o.quantity, o.unit)}</td>
                  <td>{fmtDate(o.due_date)}</td>
                  <td>{supplySource(o.source_ref, o.source_file)}</td>
                  {canWrite && (
                    <td>
                      <Button
                        type="button"
                        $variant="secondary"
                        $size="sm"
                        disabled={creatingLogist}
                        onClick={async () => {
                          onError('');
                          setNotice(null);
                          try {
                            const res = await createLogistOrder({
                              orderId: o.id,
                              project_id: projectId ?? undefined,
                            }).unwrap();
                            setNotice(
                              res.already_existed
                                ? `Заказ у Логиста по позиции «${o.position_name}» уже существует.`
                                : `Создан заказ на перевозку у Логиста для «${o.position_name}».`,
                            );
                          } catch (err) {
                            onError(
                              apiErrorMessage(err) ??
                                'Не удалось создать заказ на перевозку у Логиста.',
                            );
                          }
                        }}
                      >
                        Заказать перевозку у Логиста
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <TableCaption>
        Приходы (факт) <Badge $tone="neutral">{deliveries.length}</Badge>
      </TableCaption>
      {deliveries.length === 0 ? (
        <Muted>Приходы (поступления) не загружены.</Muted>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th scope="col">Наименование</th>
                <th scope="col">№ заказа</th>
                <th scope="col" className="num">
                  Кол-во
                </th>
                <th scope="col">Дата</th>
                <th scope="col">Источник</th>
                {canWrite && <th scope="col">Качество (АОСР)</th>}
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td>{d.position_name}</td>
                  <td>{d.order_no ?? '—'}</td>
                  <td className="num">{qtyWithUnit(d.quantity, d.unit)}</td>
                  <td>{fmtDate(d.delivery_date)}</td>
                  <td>{supplySource(d.source_ref, d.source_file)}</td>
                  {canWrite && (
                    <td>
                      <Button
                        type="button"
                        $variant="ghost"
                        $size="sm"
                        disabled={creatingLot}
                        onClick={async () => {
                          onError('');
                          setNotice(null);
                          try {
                            const res = await createLotFromDelivery({
                              deliveryId: d.id,
                              site_id: projectId ?? undefined,
                            }).unwrap();
                            setNotice(
                              `Создана партия качества № ${res.lot.lot_no ?? res.lot.id.slice(0, 8)} (${res.lot.nomenclature}).`,
                            );
                          } catch (err) {
                            onError(
                              apiErrorMessage(err) ??
                                'Не удалось создать партию материала в реестре качества.',
                            );
                          }
                        }}
                      >
                        + В партии качества
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
    </>
  );
}
